/**
 * StyledTextField Validation Hook
 *
 * Handles field-level validation with support for:
 * - Built-in validation rules (required, email, URL, etc.)
 * - Custom validation functions
 * - Async validation with proper error handling
 * - Integration with external validation systems
 */

import { useState, useCallback, useEffect } from 'react';
import { validateFieldAsync } from '../../validation';
import type {
  StyledTextFieldProps,
  ValidationHookResult,
  ValidationRule,
  ValidationError
} from './StyledTextField.types';

// ============================================================================
// VALIDATION HOOK
// ============================================================================

export const useFieldValidation = (props: StyledTextFieldProps): ValidationHookResult => {
  const {
    rules = [],
    validateEmail = false,
    validateUrl = false,
    customValidation,
    required = false,
    type,
    name,
    label,
    onValidationChange,
    error: externalError = false,
    errorMessage: externalErrorMessage,
    value,
    validateOnChange = false,
    validateOnBlur = false,
  } = props;

  // Internal validation state
  const [internalErrors, setInternalErrors] = useState<ValidationError[]>([]);

  // Combined error state
  const hasError = externalError || internalErrors.length > 0;
  const displayErrorMessage = externalErrorMessage || (internalErrors[0]?.message);

  // ============================================================================
  // VALIDATION LOGIC
  // ============================================================================

  const validateField = useCallback(async (valueToValidate: string): Promise<ValidationError[]> => {
    // Skip validation if no rules are defined
    if (!rules?.length && !validateEmail && !validateUrl && !customValidation && !required) {
      setInternalErrors([]);
      onValidationChange?.([]);
      return [];
    }

    try {
      // Build comprehensive validation rules
      const validationRules: ValidationRule[] = [...(rules || [])];

      // Add required validation if needed
      if (required) {
        validationRules.push({
          type: 'required',
          message: `${label || name || 'This field'} is required`
        });
      }

      // Add email validation if requested
      if (validateEmail || type === 'email') {
        validationRules.push({
          type: 'email',
          message: 'Please enter a valid email address'
        });
      }

      // Add URL validation if requested
      if (validateUrl || type === 'url') {
        validationRules.push({
          type: 'url',
          message: 'Please enter a valid URL'
        });
      }

      // Add phone validation for tel type
      if (type === 'tel') {
        validationRules.push({
          type: 'pattern',
          value: /^[\+]?[1-9][\d]{0,15}$/,
          message: 'Please enter a valid phone number'
        });
      }

      // Add custom validation if provided
      if (customValidation) {
        validationRules.push({
          type: 'custom',
          validator: customValidation,
          message: 'Invalid value'
        });
      }

      // Validate the field using the async validator
      const errors = await validateFieldAsync(
        valueToValidate,
        validationRules,
        name || 'field'
      );

      // Update state and notify parent
      setInternalErrors(errors);
      onValidationChange?.(errors);

      return errors;
    } catch (error) {
      console.error('Field validation error:', error);

      // Return empty array on validation error to prevent UI breaking
      const fallbackErrors: ValidationError[] = [];
      setInternalErrors(fallbackErrors);
      onValidationChange?.(fallbackErrors);

      return fallbackErrors;
    }
  }, [
    rules,
    validateEmail,
    validateUrl,
    customValidation,
    required,
    type,
    name,
    label,
    onValidationChange
  ]);

  // ============================================================================
  // INITIAL VALIDATION
  // ============================================================================

  // Initial validation on component mount - only run validation if we have validation rules
  useEffect(() => {
    const hasValidationRules = required || (rules?.length || 0) > 0 || validateEmail || validateUrl || customValidation;

    if (hasValidationRules && value) {
      // Only validate on mount if there's a value and we have validation rules
      // Use setTimeout to avoid React update depth issues
      const timeoutId = setTimeout(() => {
        validateField(value);
      }, 0);

      return () => clearTimeout(timeoutId);
    }
    /* eslint-disable-next-line */
  }, []); // Empty dependency array - only run once on mount

  // ============================================================================
  // VALIDATION UTILITIES
  // ============================================================================

  /**
   * Clear all validation errors
   */
  const clearErrors = useCallback(() => {
    setInternalErrors([]);
    onValidationChange?.([]);
  }, [onValidationChange]);

  /**
   * Check if field has specific validation rule
   */
  const hasValidationRule = useCallback((ruleType: string): boolean => {
    return (rules || []).some(rule => rule.type === ruleType) ||
           (ruleType === 'email' && (validateEmail || type === 'email')) ||
           (ruleType === 'url' && (validateUrl || type === 'url')) ||
           (ruleType === 'required' && required) ||
           (ruleType === 'custom' && !!customValidation);
  }, [rules, validateEmail, validateUrl, required, customValidation, type]);

  /**
   * Get validation message for specific rule type
   */
  const getValidationMessage = useCallback((ruleType: string): string | undefined => {
    const rule = (rules || []).find(r => r.type === ruleType);
    return rule?.message;
  }, [rules]);

  // ============================================================================
  // RETURN HOOK RESULT
  // ============================================================================

  return {
    hasError,
    displayErrorMessage,
    internalErrors,
    validateField,
    clearErrors,
    hasValidationRule,
    getValidationMessage,
  };
};

// ============================================================================
// VALIDATION EVENT HANDLERS
// ============================================================================

/**
 * Hook for handling validation-related events
 */
export const useValidationEventHandlers = (
  props: StyledTextFieldProps,
  validateField: (value: string) => Promise<ValidationError[]>
) => {
  const {
    onChange,
    onBlur,
    onFocus,
    validateOnChange = false,
    validateOnBlur = false,
    autoSelect = false,
    maxLength,
    value
  } = props;

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    // Apply maxLength constraint
    if (maxLength && newValue.length > maxLength) {
      return; // Don't allow input beyond maxLength
    }

    onChange(newValue);

    // Validate on change if enabled
    if (validateOnChange) {
      validateField(newValue);
    }
  }, [onChange, maxLength, validateOnChange, validateField]);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.(event);

    // Validate on blur if enabled
    if (validateOnBlur) {
      validateField(value);
    }
  }, [onBlur, validateOnBlur, validateField, value]);

  const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    onFocus?.(event);

    // Auto-select text if enabled
    if (autoSelect && event.target) {
      event.target.select();
    }
  }, [onFocus, autoSelect]);

  return {
    handleChange,
    handleBlur,
    handleFocus,
  };
};